import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/profilePage.css';
import '../styles/create-plan.css';
import API_CONFIG from '../config/api';

interface UserData {
  name: string;
  email: string;
  goals: string[];
  issueAreas: string[];
  ageGroup: string;
  rewindGoal: string;
  rejuvenationType: string;
  motivation: string;
}

const UserProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  const handleBackClick = () => {
    navigate('/routine');
  };

  useEffect(() => {
    document.documentElement.classList.add('profile-page');
    document.body.classList.add('profile-page');
    loadUserData();
    
    return () => {
      document.documentElement.classList.remove('profile-page');
      document.body.classList.remove('profile-page');
    };
  }, []);

  const loadUserData = async () => {
    try {
      const savedUser = localStorage.getItem('user');
      if (!savedUser) {
        navigate('/signin');
        return;
      }

      const user = JSON.parse(savedUser);
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/profile/${user.email}`);
      const data = await response.json();

      if (data.success) {
        setUserData({
          name: data.user.name,
          email: data.user.email,
          goals: data.user.goals || [],
          issueAreas: data.user.issueAreas || [],
          ageGroup: data.user.ageGroup || '',
          rewindGoal: data.user.rewindGoal || '',
          rejuvenationType: data.user.rejuvenationType || '',
          motivation: data.user.motivation || ''
        });
      } else {
        console.error('Ошибка загрузки данных пользователя:', data.message);
      }
    } catch (error) {
      console.error('Ошибка при загрузке данных пользователя:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-quiz-container">
      <div className="profile-background">
        
        <div className="top-bar">
          <div className="navbar">
            <button className="back-button goal-back-button" aria-label="Назад" onClick={handleBackClick}>
              <img 
                src="/image/arrow_left.svg" 
                alt="Back" 
                width="6" 
                height="12" 
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                  svg.setAttribute('width', '6');
                  svg.setAttribute('height', '12');
                  svg.setAttribute('viewBox', '0 0 6 12');
                  svg.innerHTML = '<path d="M5 11L1 6L5 1" stroke="#28194B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>';
                  e.currentTarget.parentNode?.appendChild(svg);
                }}
              />
            </button>
            <div className="app-icon">
              <img src="/image/rewind-icon-24px.svg" alt="" className="app-rewind-icon" />
              <span className="app-name">Profile</span>
            </div>
          </div>
        </div>

        <main className="content-wrapper">
          <div className="profile-content">
          
          <div className="profile-user-profile-section">
            <div className="profile-user-main-profile">
              <img 
                src="/image/main-user-profile.svg" 
                alt="User Profile" 
                className="profile-user-main-profile-image"
              />
            </div>
            
            <div className="profile-user-details">
              <div className="profile-user-name-section">
                <h2 className="profile-user-name">{userData?.name || 'Loading...'}</h2>
                <div className="profile-user-pro-badge">
                  <span className="profile-user-pro-text">Pro account</span>
                  <div className="profile-user-crown-icon">
                    <img 
                      src="/image/crown.svg" 
                      alt="Crown" 
                      className="profile-user-crown-image"
                    />
                  </div>
                </div>
              </div>
              <p className="profile-user-email">{userData?.email || 'Loading...'}</p>
            </div>
          </div>

          {/* Summary Card - используем те же стили что и на create-plan */}
          <div className="summary-card">
            {loading ? (
              <div style={{ padding: '16px', textAlign: 'center' }}>Loading...</div>
            ) : (
              <>
                <div className="summary-row">
                  <div className="summary-item">
                    <div className="summary-icon">
                      <img src="/image/user-icon-24px.svg" alt="User" />
                    </div>
                    <div className="summary-content">
                      <p className="summary-label">Age</p>
                      <p className="summary-value">{userData?.ageGroup || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="summary-item">
                    <div className="summary-icon">
                      <img src="/image/rewind-icon-24px.svg" alt="Rewind" />
                    </div>
                    <div className="summary-content">
                      <p className="summary-label">Rewind Goal</p>
                      <p className="summary-value">{userData?.rewindGoal || 'N/A'}</p>
                    </div>
                  </div>
                </div>
                <div className="summary-row">
                  <div className="summary-item">
                    <div className="summary-icon">
                      <img src="/image/clock-icon-24px.svg" alt="Clock" />
                    </div>
                    <div className="summary-content">
                      <p className="summary-label">Rejuvenation Type</p>
                      <p className="summary-value">{userData?.rejuvenationType || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="summary-item">
                    <div className="summary-icon">
                      <img src="/image/flash-icon-24px.svg" alt="Flash" />
                    </div>
                    <div className="summary-content">
                      <p className="summary-label">Motivation</p>
                      <p className="summary-value">{userData?.motivation || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Challenges Card - используем те же стили что и на create-plan */}
          <div className="challenges-card">
            <div className="challenges-header">
              <div className="challenges-icon">
                <img src="/image/lamp-icon-24px.svg" alt="Lamp" />
              </div>
              <div className="challenges-content">
                <p className="challenges-label">Challenges</p>
                {loading ? (
                  <div className="challenges-tags">
                    <span className="challenge-tag">Loading...</span>
                  </div>
                ) : userData?.issueAreas && userData.issueAreas.length > 0 ? (
                  <div className="challenges-tags">
                    {userData.issueAreas.map((issue, index) => (
                      <span key={index} className="challenge-tag">
                        {issue}{index < userData.issueAreas.length - 1 ? ',' : ''}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="challenges-tags">
                    <span className="challenge-tag">No challenges selected</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="profile-user-info-section">
            <div className="profile-user-section-header">
              <div className="profile-user-section-icon">
                <img 
                  src="/image/info.svg" 
                  alt="Info" 
                  className="profile-user-section-icon-image"
                />
              </div>
              <h3 className="profile-user-section-title">Info</h3>
            </div>
            
            <div className="profile-user-info-list">
              <div className="profile-user-info-item">
                <span className="profile-user-info-text">Get help</span>
                <div className="profile-user-info-arrow">
                  <img src="/image/arrow_left.svg" alt="Arrow" className="profile-user-info-arrow-image" />
                </div>
              </div>
              
              <div className="profile-user-info-item">
                <span className="profile-user-info-text">Privacy Policy</span>
                <div className="profile-user-info-arrow">
                  <img src="/image/arrow_left.svg" alt="Arrow" className="profile-user-info-arrow-image" />
                </div>
              </div>
              
              <div className="profile-user-info-item profile-user-info-item-last">
                <span className="profile-user-info-text">Terms of use</span>
                <div className="profile-user-info-arrow">
                  <img src="/image/arrow_left.svg" alt="Arrow" className="profile-user-info-arrow-image" />
                </div>
              </div>
            </div>
          </div>

          </div>
        </main>
      </div>
    </div>
  );
};

export default UserProfilePage;